import { useEffect, useState, useRef, useCallback } from "react";
import { SegmentMeter } from "@/components/SegmentMeter";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MessageQualityHint } from "@/components/MessageQualityHint";
import { smartEncode } from "@/lib/smsQuality";
import {
  MessageSquarePlus, Mail, Send, Rocket, Pause, Play, X, Loader2, Clock, Users2, FlaskConical, CheckCircle2, AlertTriangle, RefreshCw, PhoneOff, Eye, ChevronUp, ChevronDown, ShieldCheck,
} from "lucide-react";
import { V } from "@/lib/vocab";

const RATE_OPTIONS = [
  ["0", "All at once"],
  ["100", "100 / hour"],
  ["250", "250 / hour"],
  ["500", "500 / hour"],
  ["1000", "1,000 / hour"],
];

const HOUR_OPTS = Array.from({ length: 24 }, (_, h) => [h, `${(h % 12) || 12}:00 ${h < 12 ? "AM" : "PM"}`]);
const hourShort = (h) => `${(h % 12) || 12}${h < 12 ? "am" : "pm"}`;

/* Voice-aware default LAUNCH welcome (mirrors backend launch_invite_default). Placeholders
   {name}/{persona}/{community}/{capabilities} are filled by the preview + real send. */
export function launchDefault(tenant) {
  const reach = tenant?.voice_ai_enabled
    ? "text this number anytime, or call it to talk with me live,"
    : "text this number anytime";
  return `Hi {name}! {community} now has a 24/7 AI concierge. I'm {persona}. You can ${reach} for {capabilities}. Tell your ${V.lc("household")} and neighbors they can use this same number too. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out.`;
}

/* Per-send automated-assistant notice control. Lets a manager pick a Full / Short / Off notice
   (and edit it) for THIS invite blast only — never touches the community's saved disclosure.
   A shorter/omitted notice trims SMS segments (= lower cost). */
function DisclaimerControl({ mode, text, onPick, onText, testPrefix, compact = false }) {
  return (
    <div className={`rounded-xl border border-sand-200 bg-white ${compact ? "p-3" : "p-3.5"} space-y-2`} data-testid={`${testPrefix}-card`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-ink/50" /> Automated-assistant notice</span>
        <div className="flex items-center gap-0.5 rounded-full bg-sand-100 border border-sand-200 p-0.5 shrink-0">
          {[["full", "Full"], ["short", "Short"], ["off", "Off"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`${testPrefix}-${k}`} onClick={() => onPick(k)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${mode === k ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>{l}</button>
          ))}
        </div>
      </div>
      {mode === "off" ? (
        <p className="text-[11px] text-ink/55">The &ldquo;you&apos;re texting an automated assistant&rdquo; line is <b>off</b> for this send — the required opt-out notice (STOP/HELP) still goes out. This send only; your saved {V.lc("org")} disclosure is unchanged.</p>
      ) : (
        <>
          <textarea data-testid={`${testPrefix}-textarea`} value={text} onChange={(e) => onText(e.target.value)} rows={2}
            className="w-full rounded-lg border border-sand-200 px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-emerald-500/30 resize-y" />
          <p className="text-[11px] text-ink/45">Added to each text. A shorter notice means fewer SMS segments (lower cost). This send only — your saved {V.lc("org")} disclosure stays as-is.</p>
        </>
      )}
    </div>
  );
}

/* Live campaign state + polling. Polls every 4s while a launch is running/paused. */
export function useCampaign(tenantId) {
  const [campaign, setCampaign] = useState(null);
  const timer = useRef(null);
  const refresh = useCallback(async () => {
    if (!tenantId) return null;
    try {
      const r = await api.get(`/tenants/${tenantId}/households/invite-campaign`);
      setCampaign(r.data);
      return r.data;
    } catch { return null; }
  }, [tenantId]);
  useEffect(() => {
    refresh();
    return () => clearTimeout(timer.current);
  }, [refresh]);
  useEffect(() => {
    clearTimeout(timer.current);
    const active = campaign && ["running", "paused"].includes(campaign.status);
    // Keep polling even when idle (slower) so an always-mounted banner learns about a
    // campaign started elsewhere in the same session without a page reload.
    timer.current = setTimeout(refresh, active ? 4000 : 12000);
    return () => clearTimeout(timer.current);
  }, [campaign, refresh]);
  return { campaign, refresh, setCampaign };
}

function statusLine(c) {
  if (!c) return "";
  if (c.status === "running" && c.paused_reason === "quiet_hours") return "Waiting for quiet hours — resumes at 8:00 AM local";
  if (c.status === "running") return c.simulate ? "Simulating…" : "Sending…";
  if (c.status === "paused" && c.paused_reason === "hold") return `Paused after ${c.sent} sent — review, then release the rest`;
  if (c.status === "paused") return "Paused";
  if (c.status === "done") return c.simulate ? "Test run complete" : "Launch complete";
  if (c.status === "cancelled") return "Cancelled";
  return "";
}

export function CampaignProgress({ campaign, tenantId, onChange, compact = false }) {
  const [busy, setBusy] = useState(false);
  if (!campaign || campaign.status === "none") return null;
  const c = campaign;
  const total = c.total || 0;
  const processed = c.processed ?? (c.sent + (c.failed || 0));
  const pct = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const sentPct = total ? Math.min(100, Math.round((c.sent / total) * 100)) : 0;
  const active = ["running", "paused"].includes(c.status);
  const waiting = c.status === "running" && c.paused_reason === "quiet_hours";

  const act = async (verb) => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/households/invite-campaign/${c.id}/${verb}`);
      onChange?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update the launch"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="campaign-progress" className={`rounded-2xl border ${active ? "border-emerald-200 bg-emerald-50/40" : "border-sand-200 bg-white"} p-4`}>
      <div className="flex items-center gap-2.5">
        {c.status === "done" ? <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
          : c.status === "cancelled" ? <X className="h-4.5 w-4.5 text-ink/40" />
          : waiting ? <Clock className="h-4.5 w-4.5 text-amber-500" />
          : c.status === "paused" ? <Pause className="h-4.5 w-4.5 text-amber-500" />
          : <Rocket className="h-4.5 w-4.5 text-emerald-600 animate-pulse" />}
        <span data-testid="campaign-status" className="text-sm font-semibold text-ink">{statusLine(c)}</span>
        {c.simulate && <span className="text-[10px] font-bold uppercase tracking-wide rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5">Test run</span>}
        <span className="ml-auto text-xs font-semibold text-ink/60 tabular-nums" data-testid="campaign-sent">{c.sent} / {total}</span>
      </div>
      <div data-testid="campaign-bar" className="mt-3 h-2.5 rounded-full bg-sand-100 overflow-hidden flex">
        <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${sentPct}%` }} />
        {c.failed > 0 && <div className="h-full bg-danger-400 transition-[width]" style={{ width: `${Math.max(0, pct - sentPct)}%` }} />}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink/60">
        {c.channels?.includes("text") && <span className="flex items-center gap-1"><MessageSquarePlus className="h-3.5 w-3.5" /> {c.sent_sms} texts</span>}
        {c.channels?.includes("email") && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {c.sent_email} emails</span>}
        {c.failed > 0 && <span className="flex items-center gap-1 text-danger-600"><AlertTriangle className="h-3.5 w-3.5" /> {c.failed} couldn&apos;t send</span>}
        {c.rate_per_hour > 0 && active && <span>· {c.rate_per_hour}/hr</span>}
      </div>
      {active && (
        <div className="mt-3 flex items-center gap-2">
          {c.status === "running" ? (
            <button data-testid="campaign-pause-btn" disabled={busy} onClick={() => act("pause")}
              className="rounded-full border border-sand-200 px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-sand-50 transition-colors inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />} Pause
            </button>
          ) : (
            <button data-testid="campaign-resume-btn" disabled={busy} onClick={() => act("resume")}
              className="rounded-full bg-emerald-600 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} {c.paused_reason === "hold" ? "Release the rest" : "Resume"}
            </button>
          )}
          <button data-testid="campaign-cancel-btn" disabled={busy} onClick={() => act("cancel")}
            className="rounded-full px-3.5 py-1.5 text-xs font-semibold text-ink/60 hover:text-redline-600 transition-colors">Cancel</button>
        </div>
      )}
    </div>
  );
}

/* Shared preview-and-send dialog for inviting/re-inviting residents. Shows EXACTLY what will go
   out (auto-cleaned to GSM-7), lets the manager optionally edit it for this send only, and flags
   deliverability. Used by the delivery-check resend AND the resident profile "Re-invite" button. */
export function InviteConfirmDialog({ tenantId, open, onClose, personIds = [], title, onSent }) {
  const [msg, setMsg] = useState("");
  const [prev, setPrev] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [discMode, setDiscMode] = useState("full");
  const [discText, setDiscText] = useState("");
  const discTouched = useRef(false);
  const count = personIds.length;

  useEffect(() => { if (open) { setMsg(""); setPrev(null); setDiscMode("full"); setDiscText(""); discTouched.current = false; } }, [open]);

  const pickDisc = (k) => {
    discTouched.current = false;
    setDiscMode(k);
    if (k === "full") setDiscText(prev?.disclosure?.default || "");
    else if (k === "short") setDiscText(prev?.disclosure?.short || "");
  };
  const discBody = () => (discMode === "off" ? { disclosure_off: true } : (discText.trim() ? { disclosure_text: discText.trim() } : {}));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(() => {
      api.post(`/tenants/${tenantId}/households/invite-preview`, {
        custom_message: msg.trim() || undefined,
        person_id: personIds.filter(Boolean)[0] || undefined,
        ...discBody(),
      })
        .then((r) => {
          setPrev(r.data || null);
          const disc = r.data?.disclosure;
          if (disc && !discTouched.current && discMode !== "off") setDiscText(discMode === "short" ? (disc.short || "") : (disc.default || ""));
        }).catch(() => setPrev(null)).finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, msg, tenantId, discMode, discText]);

  const send = async () => {
    const ids = personIds.filter(Boolean);
    if (!ids.length) return;
    setSending(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/households/invite-resend-failed`, { person_ids: ids, message: msg.trim() || undefined, ...discBody() });
      const { resent = 0, failed = 0, skipped = 0 } = r.data || {};
      if (resent) toast.success(`Sent ${resent} invite${resent === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}${skipped ? ` · ${skipped} skipped` : ""}`);
      else {
        const why = (r.data?.details || []).find((d) => d?.error)?.error;
        toast.error(why || (skipped ? "No valid mobile number to text" : "Couldn't send the invite"));
      }
      onSent?.(ids);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="invite-confirm-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title || `Send invite to ${count} ${V.lc("person")}${count === 1 ? "" : "s"}`}</DialogTitle>
          <DialogDescription>
            {count > 1
              ? <>This is exactly what goes out{prev?.sample_name ? <> — shown for <strong>{prev.sample_name}</strong>; everyone else is greeted by their own first name</> : ""}. Edit it below if you like — changes apply to this send only.</>
              : <>This is exactly what will go out. Edit it below if you like — changes apply to this send only.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-ink/70">Message (optional)</label>
            <textarea data-testid="invite-confirm-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
              placeholder="Leave blank to use your saved invite message"
              className="mt-1.5 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 resize-y" />
            <p className="text-[11px] text-ink/45 mt-1">{"{name}"}, {`{community}`} and {"{persona}"} fill in automatically. The opt-out notice is always added.</p>
          </div>
          <DisclaimerControl mode={discMode} text={discText} onPick={pickDisc}
            onText={(v) => { discTouched.current = true; setDiscText(v); }}
            testPrefix="invite-confirm-disc" compact />
          <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700/70 mb-1.5 flex items-center gap-1"><MessageSquarePlus className="h-3 w-3" /> What sends</div>
            {loading ? (
              <div className="py-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-ink/40" /></div>
            ) : (
              <div data-testid="invite-confirm-preview" className="whitespace-pre-wrap text-sm text-ink leading-relaxed">{smartEncode(prev?.sms || "")}</div>
            )}
          </div>
          {prev?.sms && <MessageQualityHint text={smartEncode(prev.sms)} testId="invite-confirm-quality" />}
        </div>
        <DialogFooter>
          <button data-testid="invite-confirm-cancel" onClick={() => onClose?.()}
            className="rounded-full border border-sand-200 px-4 py-2 text-sm font-semibold text-ink hover:bg-sand-50 transition-colors">Cancel</button>
          <button data-testid="invite-confirm-send" disabled={sending || loading} onClick={send}
            className="rounded-full bg-redline-500 text-white px-4 py-2 text-sm font-semibold hover:bg-redline-600 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to {count}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* On-demand delivery report: reconciles recent outbound texts against Twilio's delivery status
   so a manager can see exactly who DIDN'T get their invite (and why) and re-send in one tap.
   Not auto-loaded (each check hits the Twilio API) — the manager taps to run it. */
export function DeliveryReport({ tenantId, readOnly = false, texting = true, onResent }) {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [done, setDone] = useState(new Set());
  const [filter, setFilter] = useState("failed");
  const [collapsed, setCollapsed] = useState(false);
  const [confirmIds, setConfirmIds] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadPreview = async () => {
    setPreviewOpen(true);
    if (preview) return;
    setPreviewLoading(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/households/invite-preview`, {});
      setPreview({ sms: r.data?.sms || "", subject: r.data?.email?.subject || "", sampleName: r.data?.sample_name || "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load the preview");
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const check = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get(`/tenants/${tenantId}/households/invite-delivery`, { params: { days: d } });
      setData(r.data);
      setDone(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't check delivery");
    } finally { setLoading(false); }
  };

  const openResend = (ids) => {
    const list = ids.filter((x) => x && !done.has(x));
    if (list.length) setConfirmIds(list);
  };

  const s = data?.summary || {};
  // iter252/M3 — headline the ROSTER numbers. The raw scan covers every text ever sent from
  // the community line, so its totals ("780 didn't arrive") contradicted the actionable
  // line right beneath it ("5 residents didn't get their invite").
  const rs = data?.roster_summary || {};
  const undel = data?.undelivered || [];
  const del_ = data?.delivered || [];
  const offRoster = data?.off_roster || [];
  const resendable = (data?.resendable || []).filter((x) => !done.has(x));
  const fixableCount = undel.filter((r) => r.fixable && r.person_id && !done.has(r.person_id)).length;

  return (
    <div data-testid="delivery-report-card" className="rounded-2xl border border-sand-200 bg-white p-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <PhoneOff className="h-4.5 w-4.5 text-ink/50 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink">Invite delivery check</div>
            <div className="text-[11px] text-ink/55">{collapsed && data ? `${rs.residents_failed ?? ((s.undelivered || 0) + (s.failed || 0))} ${V.lcs("person")} didn't get it · ${rs.residents_delivered ?? (s.delivered || 0)} delivered` : `See which ${V.lcs("person")} didn't receive their invite text — and why.`}</div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="delivery-preview-btn" onClick={loadPreview}
            className="rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink hover:bg-sand-50 transition-colors inline-flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5" /> Preview invite
          </button>
          <select data-testid="delivery-days-select" value={days} disabled={loading}
            onChange={(e) => { const d = Number(e.target.value); setDays(d); setCollapsed(false); check(d); }}
            className="rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink outline-none focus:ring-2 focus:ring-redline-500/30">
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button data-testid="delivery-check-btn" disabled={loading} onClick={() => { setCollapsed(false); check(); }}
            className="rounded-full bg-ink text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-ink/90 transition-colors inline-flex items-center gap-1.5">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {data ? "Re-check" : "Check delivery"}
          </button>
          {data && (
            <button data-testid="delivery-collapse-btn" onClick={() => setCollapsed((c) => !c)} title={collapsed ? "Show results" : "Hide results"}
              className="rounded-full border border-sand-200 bg-white p-1.5 text-ink/60 hover:bg-sand-50 transition-colors">
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {data && !collapsed && (
        <div className="mt-4">
          {(() => {
            const failedCount = rs.residents_failed ?? ((s.undelivered || 0) + (s.failed || 0));
            const deliveredCount = rs.residents_delivered ?? (s.delivered || 0);
            const pill = (active, tone) =>
              `inline-flex items-center gap-1 rounded-full px-2.5 py-1 border transition-colors ${active
                ? (tone === "red" ? "bg-redline-500 text-white border-redline-500"
                  : tone === "green" ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-ink text-white border-ink")
                : "bg-white text-ink/60 border-sand-200 hover:bg-sand-50"}`;
            return (
              <div data-testid="delivery-filter" className="flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                <button data-testid="delivery-filter-failed" onClick={() => setFilter("failed")} className={pill(filter === "failed", "red")}>
                  <AlertTriangle className="h-3.5 w-3.5" /> {failedCount} {failedCount === 1 ? `${V.lc("person")}` : `${V.lcs("person")}`} didn&apos;t get it
                </button>
                <button data-testid="delivery-filter-delivered" onClick={() => setFilter("delivered")} className={pill(filter === "delivered", "green")}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> {deliveredCount} delivered
                </button>
                <button data-testid="delivery-filter-all" onClick={() => setFilter("all")} className={pill(filter === "all", "ink")}>
                  All
                </button>
                {s.in_progress > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-sand-100 text-ink/60 border border-sand-200 px-2.5 py-1"><Clock className="h-3.5 w-3.5" /> {s.in_progress} in progress</span>}
                <span data-testid="delivery-scope-note" className="text-ink/40 font-medium"
                  title={`${s.total || 0} outbound texts from this number were checked against Twilio's delivery records.`}>
                  · from {s.total || 0} texts checked (last {data.days}d)
                  {offRoster.length > 0 && <> · {offRoster.length} to numbers not on your roster (ignored)</>}
                </span>
              </div>
            );
          })()}

          {(filter === "failed" || filter === "all") && (
            undel.length === 0 ? (
              <div data-testid="delivery-no-failures" className="mt-3 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> No failed invites — everything in this window was delivered.</div>
            ) : (
              <>
                {!readOnly && texting && resendable.length > 0 && (
                  <div className="mt-3 rounded-xl bg-redline-50/60 border border-redline-100 p-3 flex items-center gap-3 flex-wrap">
                    <div className="text-xs text-ink/80 flex-1 min-w-[180px]">
                      <strong>{resendable.length}</strong> {resendable.length === 1 ? `${V.lc("person")}` : `${V.lcs("person")}`} didn&apos;t get their invite.
                      {fixableCount > 0 && <> {fixableCount} failed because the message was too long — we&apos;ve fixed the encoding, so those will go through now.</>}
                    </div>
                    <button data-testid="delivery-resend-all-btn" onClick={() => openResend(resendable)}
                      className="w-full sm:w-auto justify-center rounded-full bg-redline-500 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-redline-600 transition-colors inline-flex items-center gap-1.5 shrink-0">
                      <Send className="h-3.5 w-3.5" /> Resend all {resendable.length}
                    </button>
                  </div>
                )}
                <ul data-testid="delivery-undelivered-list" className="mt-3 divide-y divide-sand-100 rounded-xl border border-sand-100">
                  {undel.map((r) => {
                    const key = r.person_id || r.phone;
                    const sent = r.person_id && done.has(r.person_id);
                    return (
                      <li key={key} data-testid={`delivery-row-${key}`} className="flex items-center gap-3 px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-ink truncate">
                            {r.name || (r.on_roster ? `${V.one("person")}` : "Not on roster")}
                            {homeLabel(r) && <span className="text-ink/50 font-normal"> · {homeLabel(r)}</span>}
                            <span className="text-ink/40 font-normal"> · {r.phone}</span>
                          </div>
                          <div className="text-[11px] text-danger-600/90 mt-0.5">{r.error_label}</div>
                        </div>
                        {sent ? (
                          <span className="text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-1 shrink-0"><CheckCircle2 className="h-3.5 w-3.5" /> Re-sent</span>
                        ) : (!readOnly && texting && r.can_resend) ? (
                          <button data-testid={`delivery-resend-${r.person_id}`} onClick={() => openResend([r.person_id])}
                            className="rounded-full border border-sand-200 px-3 py-1 text-[11px] font-semibold text-ink hover:bg-sand-50 transition-colors inline-flex items-center gap-1 shrink-0">
                            <Send className="h-3 w-3" /> Resend
                          </button>
                        ) : (
                          <span className="text-[10px] text-ink/35 shrink-0">{r.error_code === "21610" ? "Opted out" : r.on_roster ? "Manual follow-up" : "Not on roster"}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )
          )}

          {(filter === "delivered" || filter === "all") && (
            del_.length === 0 ? (
              <div data-testid="delivery-no-delivered" className="mt-3 text-sm text-ink/50">No delivered invites recorded in this window.</div>
            ) : (
              <div className="mt-3">
                {filter === "all" && <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700/70 mb-1.5">Delivered</div>}
                <ul data-testid="delivery-delivered-list" className="divide-y divide-sand-100 rounded-xl border border-sand-100">
                  {del_.map((r) => (
                    <li key={r.person_id} data-testid={`delivery-delivered-${r.person_id}`} className="flex items-center gap-3 px-3 py-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <div className="min-w-0 flex-1 text-sm text-ink truncate">
                        {r.name || `${V.one("person")}`}
                        {homeLabel(r) && <span className="text-ink/50 font-normal"> · {homeLabel(r)}</span>}
                        <span className="text-ink/40 font-normal"> · {r.phone}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="invite-preview-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite preview</DialogTitle>
            <DialogDescription>
              The exact text {V.a("person")} receives{preview?.sampleName ? ` (shown for ${preview.sampleName})` : ""}. Each person is greeted by their own first name.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-ink/40" /></div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700/70 mb-1.5 flex items-center gap-1"><MessageSquarePlus className="h-3 w-3" /> Text message</div>
                <div data-testid="invite-preview-sms" className="whitespace-pre-wrap text-sm text-ink leading-relaxed">{preview.sms}</div>
              </div>
              {preview.subject && (
                <div className="text-[11px] text-ink/55"><span className="font-semibold text-ink/70">Email subject:</span> {preview.subject}</div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <InviteConfirmDialog tenantId={tenantId} open={!!confirmIds} personIds={confirmIds || []}
        onClose={() => setConfirmIds(null)}
        onSent={(ids) => { setDone((prev) => new Set([...prev, ...ids])); onResent?.(); }} />
    </div>
  );
}

/* Slim, always-mounted banner that surfaces an in-flight launch anywhere it's placed. */
export function InviteCampaignBanner({ tenantId, onManage }) {
  const { campaign } = useCampaign(tenantId);
  if (!campaign || !["running", "paused"].includes(campaign.status)) return null;
  return (
    <div data-testid="invite-campaign-banner">
      <div className="mb-1 flex items-center justify-end">
        <button data-testid="banner-manage-btn" onClick={onManage} className="text-xs font-bold text-emerald-700 hover:underline">Manage launch →</button>
      </div>
      <CampaignProgress campaign={campaign} tenantId={tenantId} onChange={() => {}} compact />
    </div>
  );
}

export function LaunchDialog({ open, onClose, tenantId, tenant, texting = false, emailing = false, hasLaunched = false, onDone }) {
  const { campaign, refresh, setCampaign } = useCampaign(tenantId);
  const [mode, setMode] = useState("launch"); // launch | standard
  const [launchMsg, setLaunchMsg] = useState("");
  const [standardMsg, setStandardMsg] = useState("");
  const [discMode, setDiscMode] = useState("full"); // full | short | off
  const [discText, setDiscText] = useState("");
  const discTouched = useRef(false);
  const [saveDefault, setSaveDefault] = useState(false);
  const [chText, setChText] = useState(true);
  const [chEmail, setChEmail] = useState(false);
  const [scope, setScope] = useState("phoneless");
  const [rate, setRate] = useState("250");
  const [hold, setHold] = useState("");
  const [quiet, setQuiet] = useState(true);
  const [windowStart, setWindowStart] = useState(8);
  const [windowEnd, setWindowEnd] = useState(21);
  const [simulate, setSimulate] = useState(false);
  const [starting, setStarting] = useState(false);
  // preview
  const [preview, setPreview] = useState(null);
  const [previewCh, setPreviewCh] = useState("text");
  const [sampleName, setSampleName] = useState("");
  // test send
  const [testCh, setTestCh] = useState("text");
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [startNew, setStartNew] = useState(false);
  const [confirmReal, setConfirmReal] = useState(false);

  const isActive = campaign && ["running", "paused"].includes(campaign.status);
  const isTerminal = campaign && ["done", "cancelled"].includes(campaign.status);
  const hasCampaign = campaign && campaign.status && campaign.status !== "none";
  const showProgress = hasCampaign && !startNew;   // progress view (incl. terminal result)
  const curMsg = mode === "launch" ? launchMsg : standardMsg;

  const pickDisc = (k) => {
    discTouched.current = false;
    setDiscMode(k);
    if (k === "full") setDiscText(preview?.disclosure?.default || "");
    else if (k === "short") setDiscText(preview?.disclosure?.short || "");
  };
  const discBody = () => (discMode === "off" ? { disclosure_off: true } : (discText.trim() ? { disclosure_text: discText.trim() } : {}));

  // Load saved templates + reset transient state when the dialog opens.
  useEffect(() => {
    if (!open || !tenantId) return;
    setStartNew(false); setConfirmReal(false);
    setDiscMode("full"); setDiscText(""); discTouched.current = false;
    api.get(`/tenants/${tenantId}`).then((r) => {
      const t = r.data || {};
      setLaunchMsg(t.launch_message || launchDefault(tenant || t));
      setStandardMsg(t.invite_message || "");
      setWindowStart(Number.isInteger(t.send_window_start) ? t.send_window_start : 8);
      setWindowEnd(Number.isInteger(t.send_window_end) ? t.send_window_end : 21);
    }).catch(() => { setLaunchMsg(launchDefault(tenant || {})); });
    // Opening with intent to invite: if the latest campaign already finished, show the
    // config form (not the stale result screen).
    refresh().then((c) => { if (c && ["done", "cancelled"].includes(c.status)) setStartNew(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  // Channel defaults re-sync when capabilities load — props arrive async on a deep-link open.
  useEffect(() => {
    if (!open) return;
    setChText(texting); setChEmail(!texting && emailing);
    setPreviewCh(texting ? "text" : "email");
  }, [open, texting, emailing]);

  // A real (non-simulate) launch needs an explicit confirm; any config change re-arms it.
  useEffect(() => { setConfirmReal(false); }, [simulate, mode, chText, chEmail, scope, rate, hold, discMode, discText]);

  // Debounced live preview (text + email) of the exact message.
  useEffect(() => {
    if (!open || showProgress || !tenantId || !(texting || emailing)) return;
    const t = setTimeout(() => {
      api.post(`/tenants/${tenantId}/households/invite-preview`, { custom_message: (curMsg || "").trim(), ...discBody() })
        .then((r) => {
          setPreview(r.data || null); setSampleName(r.data?.sample_name || "");
          const disc = r.data?.disclosure;
          if (disc && !discTouched.current && discMode !== "off") setDiscText(discMode === "short" ? (disc.short || "") : (disc.default || ""));
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMsg, open, showProgress, tenantId, texting, emailing, discMode, discText]);

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/households/invite-test`, {
        channel: testCh, custom_message: (curMsg || "").trim() || undefined, to: (testTo || "").trim() || undefined,
        ...discBody(),
      });
      toast.success(`Test ${r.data.channel === "email" ? "email" : "text"} sent to ${r.data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the test"); }
    finally { setTestBusy(false); }
  };

  const start = async () => {
    const channels = [];
    if (chText && texting) channels.push("text");
    if (chEmail && emailing) channels.push("email");
    if (!channels.length) { toast.error("Pick at least one channel."); return; }
    if (!simulate && !confirmReal) { setConfirmReal(true); return; }  // real send → confirm first
    setStarting(true);
    try {
      const body = {
        channels, email_scope: scope, template: mode,
        custom_message: (curMsg || "").trim() || undefined,
        save_default: saveDefault || undefined,
        ...discBody(),
        rate_per_hour: Number(rate) || 0,
        hold_after: Number(hold) || 0,
        quiet_hours: quiet,
        window_start: windowStart,
        window_end: windowEnd,
        simulate,
      };
      const r = await api.post(`/tenants/${tenantId}/households/invite-campaign`, body);
      setCampaign(r.data);
      setStartNew(false);
      setConfirmReal(false);
      toast.success(simulate ? "Test run started" : "Launch started — sending in the background");
      onDone?.();
    } catch (e) {
      const msg = e.response?.data?.detail || "Couldn't start the launch";
      toast.error(msg);
      if (e.response?.status === 409) refresh();
    } finally { setStarting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="launch-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Rocket className="h-5 w-5 text-emerald-600" /> {hasLaunched ? `Invite ${V.lcs("person")}` : `Launch to your ${V.lc("org")}`}</DialogTitle>
          <DialogDescription>
            {showProgress
              ? (isActive ? "Your invites are being sent. You can pause, release or cancel here." : "Here's how your last send went.")
              : (hasLaunched
                  ? `Welcome any ${V.lcs("person")} you've added since your launch. Sending runs in the background, throttled and inside quiet hours — anyone already invited is skipped.`
                  : `Welcome every ${V.lc("person")} who hasn't been invited yet. Sending runs in the background, throttled and inside quiet hours — already-invited ${V.lcs("person")} are always skipped.`)}
          </DialogDescription>
        </DialogHeader>

        {showProgress ? (
          <div className="space-y-3">
            <CampaignProgress campaign={campaign} tenantId={tenantId} onChange={(c) => { setCampaign(c); onDone?.(); }} />
            {isActive && <p className="text-xs text-ink/50">You can safely close this — the launch keeps running. Track it here or on your dashboard.</p>}
            {isTerminal && (
              <button data-testid="campaign-start-another" onClick={() => { setStartNew(true); setConfirmReal(false); }}
                className="w-full rounded-full border border-sand-200 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-sand-50 transition-colors">
                Send more invites
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Message */}
            <div>
              <div className="flex items-center gap-1 rounded-full bg-sand-100 border border-sand-200 p-0.5 w-max">
                <button data-testid="launch-msg-mode-launch" onClick={() => setMode("launch")}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${mode === "launch" ? "bg-ink text-white" : "text-ink/60"}`}>Launch welcome</button>
                <button data-testid="launch-msg-mode-standard" onClick={() => setMode("standard")}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${mode === "standard" ? "bg-ink text-white" : "text-ink/60"}`}>Standard invite</button>
              </div>
              <textarea data-testid="launch-message-textarea" value={curMsg} onChange={(e) => mode === "launch" ? setLaunchMsg(e.target.value) : setStandardMsg(e.target.value)}
                rows={4} placeholder={mode === "standard" ? `Leave blank to use your ${V.lc("org")}'s standard welcome.` : ""}
                className="mt-2 w-full rounded-lg border border-sand-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-500/30 resize-y" />
              <SegmentMeter text={curMsg} extraChars={90} testId="launch-segment-meter" />
              <p className="text-[11px] text-ink/50 mt-1">{"{name}"}, {`{community}`} and {"{persona}"} fill in automatically. Texts always include the required opt-out notice (msg rates, STOP, HELP).</p>
              <label className="flex items-center gap-2 text-xs text-ink/70 mt-1.5 cursor-pointer">
                <input type="checkbox" data-testid="launch-save-default" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} className="h-3.5 w-3.5 accent-ink" />
                Save as my {V.lc("org")}'s default {mode === "launch" ? "launch" : "invite"} message
              </label>
            </div>

            {/* Automated-assistant notice (per-send) */}
            <DisclaimerControl mode={discMode} text={discText} onPick={pickDisc}
              onText={(v) => { discTouched.current = true; setDiscText(v); }}
              testPrefix="launch-disc" />

            {/* Preview */}
            {preview && (
              <div data-testid="launch-preview" className="rounded-xl bg-sand-50 border border-sand-200 p-3">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Preview · what {sampleName || `${V.a("person")}`} receives</div>
                  {texting && emailing && (
                    <div className="flex items-center gap-0.5 rounded-full bg-white border border-sand-200 p-0.5 shrink-0">
                      <button data-testid="launch-preview-tab-text" onClick={() => setPreviewCh("text")} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${previewCh === "text" ? "bg-ink text-white" : "text-ink/60"}`}>Text</button>
                      <button data-testid="launch-preview-tab-email" onClick={() => setPreviewCh("email")} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${previewCh === "email" ? "bg-ink text-white" : "text-ink/60"}`}>Email</button>
                    </div>
                  )}
                </div>
                {previewCh === "email" && emailing ? (
                  <div data-testid="launch-preview-email" className="rounded-lg bg-white border border-sand-200 overflow-hidden">
                    <div className="border-b border-sand-100 px-3 py-2"><div className="text-[10px] text-ink/40 uppercase">Subject</div><div className="text-[13px] font-semibold text-ink">{preview.email?.subject}</div></div>
                    <div className="px-3 py-2.5 text-[13px] text-ink whitespace-pre-wrap">{preview.email?.body}</div>
                  </div>
                ) : (
                  <div className="flex justify-start"><div data-testid="launch-preview-sms" className="max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-sand-200 px-3 py-2 text-[13px] text-ink whitespace-pre-wrap">{preview.sms}</div></div>
                )}
              </div>
            )}

            {texting && preview?.sms && (
              <MessageQualityHint text={preview.sms} testId="launch-sms-quality"
                onClean={() => { (mode === "launch" ? setLaunchMsg : setStandardMsg)((m) => smartEncode(m)); toast.success("Cleaned up special characters for better delivery"); }} />
            )}

            {/* Channels */}
            <div className="space-y-2">
              {texting && (
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="launch-channel-text" checked={chText} onChange={(e) => setChText(e.target.checked)} className="h-4 w-4 accent-ink" />
                  <MessageSquarePlus className="h-4 w-4 text-ink/60" /> Text everyone with a phone on file
                </label>
              )}
              {emailing && (
                <label className="flex items-center gap-2.5 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="launch-channel-email" checked={chEmail} onChange={(e) => setChEmail(e.target.checked)} className="h-4 w-4 accent-ink" />
                  <Mail className="h-4 w-4 text-ink/60" /> Email {V.lcs("person")} {scope === "phoneless" ? "with no phone" : "with an email"}
                </label>
              )}
              {chEmail && emailing && (
                <div className="ml-6 flex items-center gap-3 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" data-testid="launch-scope-phoneless" checked={scope === "phoneless"} onChange={() => setScope("phoneless")} className="h-3.5 w-3.5 accent-ink" /> Only phoneless (no double-contact)</label>
                  <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" data-testid="launch-scope-all" checked={scope === "all"} onChange={() => setScope("all")} className="h-3.5 w-3.5 accent-ink" /> Everyone with email</label>
                </div>
              )}
            </div>

            {/* Send options */}
            <div className="rounded-xl border border-sand-200 bg-white p-3.5 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-ink flex items-center gap-1.5"><Send className="h-4 w-4 text-ink/50" /> Release rate</span>
                <select data-testid="launch-rate-select" value={rate} onChange={(e) => setRate(e.target.value)}
                  className="rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm bg-white outline-none">
                  {RATE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-ink flex items-center gap-1.5"><Users2 className="h-4 w-4 text-ink/50" /> Pause after first
                  <span className="text-ink/50 font-normal">(waves — optional)</span></span>
                <input data-testid="launch-hold-input" type="number" min="0" value={hold} onChange={(e) => setHold(e.target.value)} placeholder="all"
                  className="w-24 rounded-lg border border-sand-200 px-2.5 py-1.5 text-sm outline-none text-right" />
              </div>
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm font-medium text-ink flex items-center gap-1.5"><Clock className="h-4 w-4 text-ink/50" /> Only send {hourShort(windowStart)}–{hourShort(windowEnd)} local</span>
                <input type="checkbox" data-testid="launch-quiet-toggle" checked={quiet} onChange={(e) => setQuiet(e.target.checked)} className="h-4.5 w-4.5 accent-emerald-600" />
              </label>
              {quiet && (
                <div className="flex items-center gap-2 pl-6 -mt-1 flex-wrap">
                  <span className="text-xs text-ink/55">Between</span>
                  <select data-testid="launch-window-start" value={windowStart} onChange={(e) => setWindowStart(Number(e.target.value))}
                    className="rounded-lg border border-sand-200 px-2 py-1 text-xs bg-white outline-none">
                    {HOUR_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <span className="text-xs text-ink/55">and</span>
                  <select data-testid="launch-window-end" value={windowEnd} onChange={(e) => setWindowEnd(Number(e.target.value))}
                    className="rounded-lg border border-sand-200 px-2 py-1 text-xs bg-white outline-none">
                    {HOUR_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {windowEnd <= windowStart && <span className="text-xs text-redline-600">End must be after start</span>}
                </div>
              )}
              {!quiet && (
                <p className="text-xs text-amber-600 pl-6 -mt-1 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Off — invites can send at any hour, including overnight.</p>
              )}
              <label className="flex items-center justify-between gap-3 cursor-pointer border-t border-sand-100 pt-3">
                <span className="text-sm font-medium text-ink flex items-center gap-1.5"><FlaskConical className="h-4 w-4 text-violet-500" /> Test run — simulate, send nothing</span>
                <input type="checkbox" data-testid="launch-simulate-toggle" checked={simulate} onChange={(e) => setSimulate(e.target.checked)} className="h-4.5 w-4.5 accent-violet-600" />
              </label>
            </div>

            {/* Send test to me */}
            <div className="rounded-xl border border-dashed border-sand-300 p-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-ink/70">Send a test to me:</span>
              <select data-testid="launch-test-channel" value={testCh} onChange={(e) => setTestCh(e.target.value)} className="rounded-lg border border-sand-200 px-2 py-1 text-xs bg-white outline-none">
                {texting && <option value="text">Text</option>}
                {emailing && <option value="email">Email</option>}
              </select>
              <input data-testid="launch-test-to-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={testCh === "text" ? "my mobile (optional)" : "my email (optional)"}
                className="flex-1 min-w-[140px] rounded-lg border border-sand-200 px-2.5 py-1 text-xs outline-none" />
              <button data-testid="launch-test-send-btn" disabled={testBusy} onClick={sendTest}
                className="rounded-full bg-ink text-white px-3 py-1.5 text-xs font-semibold hover:bg-ink-700 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                {testBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test
              </button>
            </div>

            {!simulate && confirmReal && (
              <div data-testid="launch-real-warning" className="rounded-lg bg-redline-50 border border-redline-200 text-redline-700 text-xs px-3 py-2.5 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>You're about to message every {V.lc("person")} who hasn't been invited yet — <b>for real</b>. This can&apos;t be undone. Press the red button again to confirm, or turn on the Test run above to rehearse.</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button data-testid="launch-close" onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold text-ink-700 hover:bg-sand-100 transition-colors">{showProgress ? "Close" : "Cancel"}</button>
          {!showProgress && (
            <button data-testid="launch-start-btn" disabled={starting || (!chText && !chEmail) || (quiet && windowEnd <= windowStart)} onClick={start}
              className={`rounded-full font-semibold px-6 py-2.5 text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2 ${(!simulate && confirmReal) ? "bg-redline-600 hover:bg-redline-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : (!simulate && confirmReal) ? <AlertTriangle className="h-4 w-4" /> : <Rocket className="h-4 w-4" />}
              {simulate ? "Start test run"
                : confirmReal ? (hasLaunched ? "Yes — invite them" : "Yes — send for real")
                : (hasLaunched ? `Invite newly-added ${V.lcs("person")}` : "Start launch")}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
